import { InputAdornment, TextField } from '@mui/material';
import { memo, useMemo } from 'react';
import { Icon } from '../icon';
import type { OutlinedInputProps as Props } from './types';

export const OutlinedInput = memo(
  ({
    startIcon,
    endIcon,
    onClickEndIcon,
    onClickStartIcon,
    sx,
    ...props
  }: Props) => {
    const rest = props;

    const iconSize = useMemo(() => {
      const xsmallSize = props.size === 'xsmall';
      const smallSize = props.size === 'small';
      const mediumSize = props.size === 'medium';
      const largeSize = props.size === 'large';

      return xsmallSize
        ? 12
        : smallSize
        ? 16
        : mediumSize
        ? 20
        : largeSize
        ? 24
        : 20; // default medium size
    }, [props.size]);

    const startIconSx = useMemo(() => {
      return {
        cursor: onClickStartIcon ? 'pointer' : 'default',
      };
    }, [onClickStartIcon]);

    const endIconSx = useMemo(() => {
      return {
        cursor: onClickEndIcon ? 'pointer' : 'default',
      };
    }, [onClickEndIcon]);

    return (
      <TextField
        {...rest}
        variant="outlined"
        sx={sx}
        fullWidth={props.fullWidth}
        error={!!props.error}
        helperText={props.helperText}
        placeholder={props.placeholder}
        InputLabelProps={{
          ...props.InputLabelProps,
          shrink: props.InputLabelProps?.shrink ?? Boolean(props.placeholder),
        }}
        InputProps={{
          startAdornment: startIcon && (
            <InputAdornment position="start">
              <Icon
                name={startIcon}
                size={iconSize}
                color={props.error ? 'error.500' : 'grey.500'}
                onClick={onClickStartIcon}
                sxx={startIconSx}
              />
            </InputAdornment>
          ),
          endAdornment: endIcon && (
            <InputAdornment position="end">
              <Icon
                name={endIcon}
                size={iconSize}
                color={props.error ? 'error.500' : 'grey.500'}
                onClick={onClickEndIcon}
                sxx={endIconSx}
              />
            </InputAdornment>
          ),
        }}
      />
    );
  }
);

OutlinedInput.displayName = 'OutlinedInput';
